import type { QueryClient } from '@tanstack/react-query';
import createClient, { type Middleware } from 'openapi-fetch';
import createOpenapiQueryClient from 'openapi-react-query';
import type { paths } from './penta-api-types';

export type PentaApiPaths = paths;

export interface PentaApiClientParams {
  baseUrl?: string;
  queryClient: QueryClient;
  middlewares?: Middleware[];
}

// Singleton instance storage
let pentaApiClientInstance: {
  rqClient: ReturnType<typeof createOpenapiQueryClient<PentaApiPaths>>;
  fetchClient: ReturnType<typeof createClient<PentaApiPaths>>;
  queryClient: QueryClient;
} | null = null;

// Default middlewares can be defined here
const defaultMiddlewares: Middleware[] = [
  // Example: Logging middleware
  // {
  //   onRequest: ({ request }) => {
  //     console.log('Request:', request);
  //     return request;
  //   }
  // }
];

/**
 * Creates and initializes the Penta API client.
 * This sets up the fetch client (openapi-fetch) and the React Query wrapper (openapi-react-query).
 */
export const createPentaApiClient = (params: PentaApiClientParams) => {
  // 1. Initialize openapi-fetch client
  const fetchClient = createClient<PentaApiPaths>({
    baseUrl: params.baseUrl,
  });

  // 2. Register middlewares (defaults + provided)
  const allMiddlewares = [...defaultMiddlewares, ...(params.middlewares || [])];

  allMiddlewares.forEach((mw) => {
    fetchClient.use(mw);
  });

  // 3. Initialize openapi-react-query client
  // Note: Retry logic is primarily handled by QueryClient configuration passed in `params.queryClient`
  const rqClient = createOpenapiQueryClient(fetchClient);

  // 4. Store singleton
  pentaApiClientInstance = {
    fetchClient,
    rqClient,
    queryClient: params.queryClient,
  };

  return pentaApiClientInstance;
};

/**
 * Get the initialized React Query API client.
 * Use this in non-React contexts or hooks if strictly necessary.
 */
export const getPentaApiRq = () => {
  if (!pentaApiClientInstance) {
    throw new Error(
      'PentaApi client not initialized. Call createPentaApiClient() first.',
    );
  }
  return pentaApiClientInstance.rqClient;
};

/**
 * Get the underlying query client.
 */
export const getPentaApiQueryClient = () => {
  if (!pentaApiClientInstance) {
    throw new Error(
      'PentaApi client not initialized. Call createPentaApiClient() first.',
    );
  }
  return pentaApiClientInstance.queryClient;
};

/**
 * Get the underlying fetch client (openapi-fetch).
 * Use this only when you need direct fetch access outside React Query patterns.
 * For most use cases, prefer usePentaRq() in React components or getPentaApiRq() elsewhere.
 */
export const getPentaApiFetchClient = () => {
  if (!pentaApiClientInstance) {
    throw new Error(
      'PentaApi client not initialized. Call createPentaApiClient() first.',
    );
  }
  return pentaApiClientInstance.fetchClient;
};
